// apps/portals/student/src/app.tsx — the OPAC shell the root route mounts.
// Replaces router.tsx's former bare `() => <Outlet />`.
//
// THE ROOT STILL GATES NOTHING — router.tsx's public-aware choice is intact and
// is doubly load-bearing here: the catalogue must render for anonymous
// visitors. This component only decides whether to draw CHROME, and it draws it
// for anonymous visitors too — signed out, the sidebar simply shows the public
// catalogue entries and the header shows a Sign in link instead of an account
// menu. Access is still decided per-screen (AccountLayout re-checks lib:opac).
import { Show, Suspense } from "solid-js";
import { Outlet, useNavigate, useRouterState } from "@tanstack/solid-router";
import { useQuery } from "@tanstack/solid-query";
import { useCapabilities, useSession } from "@erp/auth-kit/fe";
import { libraryQueries } from "@erp/module-lib/fe";
import { signOutUser } from "@erp/module-auth/fe";
import { AppShell } from "@erp/ui/app-shell";
import { LinkButton } from "@erp/ui/link-button";
import { PageSkeleton } from "@erp/ui/page-skeleton";
import { LogIn } from "lucide-solid";
import { Toaster } from "@erp/ui/toast";
import { studentNav } from "./nav";

/**
 * Screens that render without chrome — the door. /register joins /login
 * (the reading gate, ADR-010 §2.12): a reader arriving from the website has
 * no account yet, and the OPAC's sidebar would only be noise beside the
 * form. /verify-email is NOT here — that person is signed in, and the chrome
 * (their account menu) is theirs.
 */
const CHROMELESS = ["/login", "/register", "/verify", "/accept-invite", "/reset-password"];

export function App() {
  const auth = useSession();
  const caps = useCapabilities();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // NEVER read auth.session() (solid-query's q.data) while /me is pending: that
  // read SUSPENDS, and the shell is the outermost component, so there is no
  // boundary above it — the whole page would blank until /me answers. Read
  // isPending first, q.data only after. (Regression-tested in the admin portal;
  // the same hazard applies to every shell.)
  const session = () => (auth.loading() ? null : auth.session());
  // The sidebar waits for BOTH answers — /me and the install's capabilities
  // manifest (ERP-DEPLOYMENT-MODEL §13) — and draws nothing until it has them,
  // exactly as the header's Sign in link waits for /me: a half-known nav would
  // flash entries that then vanish. Once both are in hand, anonymous (a
  // RESOLVED null) still gets the public catalogue entries — the public-aware
  // contract is untouched; only the pending state is quiet. `caps.modules()`
  // is the enabled set, so an install without `lib` offers no catalogue at all.
  const modules = () => (auth.loading() || caps.loading() ? [] : studentNav(session(), caps.modules()));

  // The OPAC's saved branding — publicTitle names the shell. Anonymous-readable;
  // the theme's own name shows until the settings answer. The `accent` is a
  // categorization hue NAME since D2 / db-lib 0022 and no longer retints
  // --color-primary here: a category hue is never an accent (DEV-GUIDE-002
  // §12.3 / §12.5), and "indigo" / "cyan" / "yellow" are CSS named colours,
  // so the old `--color-primary: ${accent}` would have painted the wrong
  // colour silently. The shell has no identity slot for it yet — owed
  // (modules/lib/decisions.md 2026-09-19).
  //
  // Gated on the manifest (PORTALS-3, 2026-09-20): this is a LIB read, and an
  // install without `lib` has no OPAC settings row to name the shell — the
  // gateway answers 404 MODULE_DISABLED. So the read waits for the manifest
  // and fires only when lib is enabled; the fallback name below serves the
  // rest. Outside a provider `caps` is the unrestricted default, so every
  // screen test that mounts the shell bare still sees the read fire.
  const branding = useQuery(() => ({ ...libraryQueries.opacSettings(), enabled: !caps.loading() && caps.has("lib") }));
  const bare = () => CHROMELESS.some((p) => pathname() === p || pathname().startsWith(`${p}/`));


  const signOut = async () => {
    try {
      await signOutUser();
    } finally {
      await auth.refetch();
      await navigate({ to: "/" }); // back to the PUBLIC catalogue, not the door
    }
  };

  return (
    <>
      <Toaster />
      <Show
        when={!bare()}
        fallback={
          /* A <div>, not a <main> (P-07, 2026-09-21): on a bare route the SCREEN owns the
             landmark — the login and register doors and ScannerApp each draw their own
             <main>, framed to their own width — and a shell <main> around them made two
             landmarks per page. A bare wire that mounts a module component drawing none
             (CardVerify, AcceptInviteScreen, PasswordResetScreen) adds the <main> itself. */
          <div class="min-h-screen">
            <Outlet />
          </div>
        }
      >
        <AppShell
          modules={modules()}
          pathname={pathname()}
          user={session() ? { name: session()?.name ?? session()?.email ?? "My account", email: session()?.email } : null}
          onSignOut={signOut}
          onProfile={() => void navigate({ to: "/account" })}
          appName={branding.data?.publicTitle || "Library catalogue"}
          headerActions={
            <Show when={!session() && !auth.loading()}>
              <LinkButton to="/login" variant="primary" size="sm">
                <LogIn size={14} aria-hidden="true" />
                Sign in
              </LinkButton>
            </Show>
          }
        >
          <Suspense fallback={<PageSkeleton id="route-skeleton" />}>
            <Outlet />
          </Suspense>
        </AppShell>
      </Show>
    </>
  );
}
